import React, { useState } from "react";
import Nav from "./Nav";
import HeaderSticky from "./HeaderSticky";
import ResponsiveMenu from "./ResponsiveMenu";
import { useHappyContext } from "../../context/happy_context.js";
import { Modal } from "react-bootstrap";
import Lottie from "react-lottie";
import * as animationData from "../../assets/animations/sign-up.json";
import axios from "axios";
import Swal from "sweetalert2";
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const LangDropdown = ({
    t, i18n 
}) => {
    const [open, setOpen] = useState(false);
    const current = (i18n.language && i18n.language.substring(0,2)) || 'hu';
    const options = [
        {
            code: 'hu',
            label: t('header.hu'),
            flag: '🇭🇺' 
        },
        {
            code: 'en',
            label: t('header.en'),
            flag: '🇬🇧' 
        },
        {
            code: 'de',
            label: t('header.de'),
            flag: '🇩🇪' 
        },
    ];
    const currentOpt = options.find(o => o.code === current) || options[0];

    const selectLang = (lng) => {
        i18n.changeLanguage(lng);
        try {
            localStorage.setItem('i18nextLng', lng); 
        } catch(e) { /* ignore */ }
        setOpen(false);
    };

    const containerRef = React.useRef(null);

    React.useEffect(() => {
        const onDocClick = (e) => {
            if (!containerRef.current) {
                return;
            }
            if (!containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };

        document.addEventListener('click', onDocClick);

        return () => document.removeEventListener('click', onDocClick);
    }, []);

    const onKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            setOpen(false);
        }
    };

    const ui = {
        trigger: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 10px',
            borderRadius: 12,
            background: 'linear-gradient(135deg, #ffffff 0%, #f4f6f8 100%)',
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
            transition: 'all .2s ease',
            cursor: 'pointer'
        },
        caret: {
            marginLeft: 6,
            fontSize: 18,
            opacity: .7 
        },
        flag: {
            fontSize: 18,
            lineHeight: 1 
        },
        code: {
            minWidth: 24,
            textTransform: 'uppercase',
            fontWeight: 600,
            letterSpacing: .5 
        },
        menu: {
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            width: 220,
            background: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 14,
            boxShadow: '0 18px 40px rgba(0,0,0,.12)',
            padding: 8,
            zIndex: 1000
        },
        item: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'transparent',
            border: 'none',
            textAlign: 'left',
            cursor: 'pointer',
            color: '#111827',
            transition: 'background .15s ease'
        },
        itemActive: {
            background: '#eef6ff',
            color: '#0ea5e9' 
        },
        itemLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: 10 
        },
        label: { opacity: .9 },
        check: {
            fontSize: 18,
            color: '#0ea5e9' 
        }
    };

    return (
        <div ref={containerRef} style={{ position:'relative' }} onKeyDown={onKeyDown}>
            <button
                className="lang-trigger"
                onClick={() => setOpen(!open)}
                aria-haspopup="listbox"
                aria-expanded={open}
                style={ui.trigger}
            >
                <span style={ui.flag}>{currentOpt.flag}</span>
                <span style={ui.code}>{current.toUpperCase()}</span>
                <i className="ri-arrow-down-s-line" style={ui.caret}></i>
            </button>
            {open && (
                <div role="listbox" style={ui.menu}>
                    {options.map(opt => (
                        <button
                            key={opt.code}
                            onClick={() => selectLang(opt.code)}
                            role="option"
                            aria-selected={opt.code===current}
                            style={{
                                ...ui.item,
                                ...(opt.code===current ? ui.itemActive : {}) 
                            }}
                        >
                            <span style={ui.itemLeft}>
                                <span style={ui.flag}>{opt.flag}</span>
                                <span style={{ textTransform:'uppercase' }}>{opt.code}</span>
                                <span style={ui.label}>{opt.label}</span>
                            </span>
                            {opt.code===current && <i className="ri-check-line" style={ui.check}></i>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const HeaderOne = ({
    styles, disableSticky 
}) => {
    const {
        t, i18n 
    } = useTranslation();
    const {
        isLoggedIn, setIsLoggedIn 
    } = useHappyContext();
    const [offcanvasShow, setOffcanvasShow] = useState(false);
    const [searchPopup, setSearchPopup] = useState(false);
    const [email, setEmail] = useState(undefined);

    const [showLoginModal, setShowLoginModal] = React.useState(false);

    const onCanvasHandler = () => {
        setOffcanvasShow((prevState) => !prevState);
    };

    const onSearchHandler = () => {
        setSearchPopup((prevState) => !prevState);
    };

    if (searchPopup) {
        document.body.classList.add("search-popup-active");
    } else {
        document.body.classList.remove("search-popup-active");
    }

    const sticky = HeaderSticky(200);
    const classes = sticky ? "sticky" : "";
    const stickyStatus = disableSticky ? "" : " header-sticky";

    const brandAltText = t('footer.brandAlt'); // Declare t('footer.brandAlt') as a constant

    return (
        <React.Fragment>
            <Modal
                show={showLoginModal}
                onHide={() => setShowLoginModal(false)}
                centered
                size="lg"
            >
                <Modal.Body>
                    <div
                        className="eduvibe-contact-us edu-contact-us-area bg-color-white"
                        style={{ padding: "2rem" }}
                    >
                        <div
                            className="container eduvibe-animated-shape"
                            style={{
                                maxWidth: "100%",
                                width: "100%" 
                            }}
                        >
                            <div className="row g-5">
                                <div className="col-lg-12">
                                    <div className="section-title text-center">
                                        <h5 className="title">{t('common.login')}</h5>
                                        <small className="pre-title">
                                            {t('common.loginSubtitle')}
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <div className="row g-5 mt--20 d-flex align-items-center">
                                <div className="col-6">
                                    <Lottie
                                        options={{
                                            loop: true,
                                            autoplay: true,
                                            animationData: animationData,
                                            rendererSettings: {
                                                preserveAspectRatio: "xMidYMid cover",
                                            },
                                        }}
                                        width={"35rem"}
                                        height={"35rem"}
                                    />
                                </div>
                                <div className="col-6">
                                    <div className="form-group">
                                        <input
                                            autoFocus
                                            autoCapitalize
                                            type="email"
                                            className="form-control form-control-lg"
                                            name="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder={t('common.emailPlaceholder')}
                                            required
                                        />
                                    </div>
                                    <div className="form-group d-flex justify-content-center">
                                        <button
                                            className="rn-btn edu-btn"
                                            style={{ margin: "0 auto" }}
                                            onClick={() => {
                                                axios({
                                                    method: "post",
                                                    url: `${window.api}/company/auth/login`,
                                                    headers: { "Content-Type": "application/json" },
                                                    data: {
                                                        email: email,
                                                    },
                                                })
                                                    .then((res) => {
                                                        if (res.data.success) {
                                                            setIsLoggedIn(true);
                                                            localStorage.setItem("email", email);
                                                            setShowLoginModal(false);
                                                        } else {
                                                            Swal.fire({
                                                                title: t('common.loginFailed'),
                                                                confirmButtonColor: "#f1b44c",
                                                            });
                                                        }
                                                    })
                                                    .catch((err) => {
                                                        console.log(err);
                                                        Swal.fire({
                                                            title: t('common.loginFailed'),
                                                            confirmButtonColor: "#f1b44c",
                                                        });
                                                    });
                                            }}
                                        >
                                            {t('common.loginSubmit')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
            <header
                className={`header-two edu-header ${stickyStatus} ${styles || ""} ${
                    classes || ""
                }`}
            >
                <div className="header-row">
                    <div className="header-logo">
                        <Link
                            to="/"
                            className="logo"
                        >
                            <img
                                className="logo-light"
                                src="https://images.happysolutions.hu/logo.png"
                                alt={brandAltText}
                            />
                            <span className="logo-wordmark">{t('common.appName')}</span>
                        </Link>
                    </div>
                    <div className="header-nav" style={{ flexGrow: 1 }}>
                        <nav className="mainmenu-nav d-none d-lg-block">
                            <Nav/>
                        </nav>
                    </div>
                    <div className="header-right" style={{
                        display:'flex',
                        alignItems:'center',
                        gap:'1rem' 
                    }}>
                        <Link
                            to="/foglalas"
                            className="header-demo-cta d-none d-xl-inline-flex"
                        >
                            {t('nav.support', { defaultValue: 'Kérjen ingyenes bemutatót!' })}
                        </Link>
                        <div className="lang-switcher d-none d-md-flex" style={{
                            alignItems:'center',
                            gap: '0.5rem',
                            position:'relative' 
                        }}>
                            <span style={{
                                fontSize: 12,
                                opacity: 0.8 
                            }}>{t('header.language')}:</span>
                            <LangDropdown t={t} i18n={i18n} />
                        </div>
                        <div className="header-menu-bar">
                            {false && (
                                <div className="quote-icon quote-user d-none d-md-block ml--15 ml_sm--5">
                                    <button
                                        className={`edu-btn btn-medium left-icon header-button`}
                                        style={isLoggedIn ? { background: "#231f40" } : {}}
                                        onClick={() => {
                                            if (isLoggedIn) {
                                                setIsLoggedIn(false);
                                                localStorage.setItem("isLoggedIn", false);
                                                localStorage.setItem("email", "");
                                            } else {
                                                setShowLoginModal(true);
                                            }
                                        }}
                                    >
                                        <i className="ri-user-line"></i>
                                        {isLoggedIn ? "Kijelentkezés" : "Bejelentkezés"}
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="mobile-menu-bar ml--15 ml_sm--5 d-block d-xl-none">
                            <div className="hamberger">
                                <button
                                    type="button"
                                    className={`white-box-icon hamberger-button header-menu ${offcanvasShow ? 'is-open' : ''}`}
                                    onClick={onCanvasHandler}
                                    aria-label={offcanvasShow ? 'Menü bezárása' : 'Menü megnyitása'}
                                    aria-expanded={offcanvasShow}
                                >
                                    <i className={offcanvasShow ? 'ri-close-line' : 'ri-menu-line'}></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
            <ResponsiveMenu
                show={offcanvasShow}
                onClose={onCanvasHandler}
                showSearch={searchPopup}
                onSearch={onSearchHandler}
            />
        </React.Fragment>
    );
};

export default HeaderOne;